import { isNonEmpty } from '@sgme/fp';
import classnames from 'classnames';
import { PanelGroup } from 'components/shared/PanelGroup/PanelGroup';
import { WithToggle } from 'components/shared/WithToggle/WithToggle';
import { CollapseErrorDetails } from 'components/shared/WorkflowError/CollapseDetails';
import { useWorkflowErrorsUtils } from 'hooks/useWorkflowErrorsUtils';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import { FC } from 'react';
import { Alert } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { WorkflowErrorDetailsItem } from './WorkflowErrorDetailsItem';

export interface WorkflowErrorDetailsListProps {
  height?: string;
  shouldDisplayErrorDetails?: boolean;
  useLegacyWorkFlowError?: boolean;
  useMaxHeight?: boolean;
  workflowErrors: readonly NextWorkflowError[];
}

const id = 'errors-panel-details-list';

// TODO: Unit test
export const WorkflowErrorDetailsList: FC<WorkflowErrorDetailsListProps> = ({
  height,
  shouldDisplayErrorDetails = false,
  useLegacyWorkFlowError = false,
  useMaxHeight = false,
  workflowErrors,
}) => {
  const { formatMessage } = useIntl();
  const { getWorkflowErrorMessage } = useWorkflowErrorsUtils();

  const hasErrors = isNonEmpty(workflowErrors);
  const hasOnlyOneError = workflowErrors.length === 1;
  const isInitiallyOn = hasOnlyOneError && shouldDisplayErrorDetails;

  return (
    <ScrollableDiv
      className={classnames(`${id}-component}`, {
        'has-only-one-error': hasOnlyOneError,
      })}
      data-e2e={id}
      $height={height}
      id={id}
      $useMaxHeight={useMaxHeight}
    >
      {hasErrors && (
        <PanelGroup e2e={`${id}-panel-group`} id={`${id}-panel-group`}>
          {workflowErrors.map((workflowError, index: number) => (
            <WithToggle
              isInitiallyOn={isInitiallyOn}
              key={`${workflowError.rejectionCode}-${workflowError.originMessage}`}
            >
              {({ isOn, toggle }) => (
                <div
                  className={classnames({
                    'mt-2': index > 0,
                    'border-danger border': isOn,
                  })}
                  data-e2e={`${id}-panel`}
                >
                  <CollapseErrorDetails
                    canOpen={shouldDisplayErrorDetails}
                    isOpen={isOn}
                    toggleOpen={toggle}
                    title={formatPanelTitle(getWorkflowErrorMessage(workflowError))}
                  >
                    <WorkflowErrorDetailsItem
                      useLegacyWorkFlowError={useLegacyWorkFlowError}
                      workflowError={workflowError}
                    />
                  </CollapseErrorDetails>
                </div>
              )}
            </WithToggle>
          ))}
        </PanelGroup>
      )}

      {!hasErrors && (
        <Alert variant="danger">
          <strong>{formatMessage({ id: 'errorDetails.errorDetailsCanNotBeDisplayed' })}</strong>
          &nbsp;
          {formatMessage({ id: 'pleaseContactSupport' })}
        </Alert>
      )}
    </ScrollableDiv>
  );
};

const maxLengthForPanelTitle = 90;

const formatPanelTitle = (title: string): string => {
  return title.length > maxLengthForPanelTitle ? title.slice(0, maxLengthForPanelTitle).trim() + '...' : title;
};

interface ScrollableDivProps {
  $height?: string;
  $useMaxHeight?: boolean;
}
const ScrollableDiv = styled.div<ScrollableDivProps>`
  overflow-y: auto;
  ${p => p.$useMaxHeight && 'max-'}height: ${p => p.$height ?? '40em'};
`;
